import { useState, useEffect } from 'react'
import {
  Layers,
  Plus,
  Trash2,
  Save,
  CheckCircle2,
  FileText,
  Settings2,
  HelpCircle,
  Eye,
  Sliders,
  ArrowUp,
  ArrowDown,
  ListPlus,
  Info,
  Send,
  Upload,
  Sparkles,
  Zap,
} from 'lucide-react'
import { Badge, Button, Card, CardContent, Input } from '@/components/ui'
import { SectionHeading } from '@/components/common'
import { useFormConfigQuery, useUpdateFormConfigMutation } from '../../hooks/use-admin'

export function FormBuilderPage() {
  const [formType, setFormType] = useState<'SELLER' | 'DEALER'>('SELLER')
  const { data: formConfig, isLoading } = useFormConfigQuery(formType)
  const updateMutation = useUpdateFormConfigMutation()

  const [activeTab, setActiveTab] = useState<'EDITOR' | 'PREVIEW' | 'SPLIT'>('SPLIT')
  const [fields, setFields] = useState<any[]>([])
  const [documents, setDocuments] = useState<any[]>([])
  const [settings, setSettings] = useState<any>({
    autoApproval: false,
    requirePayment: false,
    resubmissionAllowed: true,
    maxAttempts: 3,
  })

  // Live preview interactive state
  const [previewValues, setPreviewValues] = useState<Record<string, any>>({})

  useEffect(() => {
    if (formConfig) {
      setFields(formConfig.fields ?? [])
      setDocuments(formConfig.documents ?? [])
      setSettings(formConfig.settings ?? {})
    }
  }, [formConfig])

  const handleAddField = () => {
    const newField = {
      id: `f_${Date.now()}`,
      name: `field_${fields.length + 1}`,
      label: 'New Form Question / Field',
      type: 'text',
      required: true,
      options: ['Option 1', 'Option 2'],
      displayOrder: fields.length + 1,
      tooltip: '',
      helpText: '',
      dependsOn: '',
      showIfValue: '',
    }
    setFields([...fields, newField])
  }

  const handleRemoveField = (index: number) => {
    setFields(fields.filter((_, i) => i !== index))
  }

  const handleMoveField = (index: number, direction: 'UP' | 'DOWN') => {
    const targetIdx = direction === 'UP' ? index - 1 : index + 1
    if (targetIdx < 0 || targetIdx >= fields.length) return
    const updated = [...fields]
    const temp = updated[index]
    updated[index] = updated[targetIdx]
    updated[targetIdx] = temp
    setFields(updated)
  }

  const handleFieldChange = (index: number, key: string, value: any) => {
    const updated = [...fields]
    updated[index] = { ...updated[index], [key]: value }
    setFields(updated)
  }

  const handleAddOption = (fieldIdx: number) => {
    const updated = [...fields]
    const currentOptions = updated[fieldIdx].options || []
    updated[fieldIdx].options = [...currentOptions, `Option ${currentOptions.length + 1}`]
    setFields(updated)
  }

  const handleRemoveOption = (fieldIdx: number, optionIdx: number) => {
    const updated = [...fields]
    const currentOptions = updated[fieldIdx].options || []
    updated[fieldIdx].options = currentOptions.filter((_: any, i: number) => i !== optionIdx)
    setFields(updated)
  }

  const handleOptionChange = (fieldIdx: number, optionIdx: number, val: string) => {
    const updated = [...fields]
    const currentOptions = [...(updated[fieldIdx].options || [])]
    currentOptions[optionIdx] = val
    updated[fieldIdx].options = currentOptions
    setFields(updated)
  }

  const handleAddDocument = () => {
    const newDoc = {
      id: `doc_${Date.now()}`,
      name: 'Verification Certificate (PDF/JPG)',
      required: true,
      acceptedFormats: ['.pdf', '.jpg', '.png'],
      maxMb: 5,
    }
    setDocuments([...documents, newDoc])
  }

  const handleSaveConfig = async () => {
    await updateMutation.mutateAsync({
      formType,
      payload: { fields, documents, settings },
    })
  }

  // Evaluate conditional logic for live preview
  const isFieldVisibleInPreview = (field: any) => {
    if (!field.dependsOn) return true
    const parentValue = previewValues[field.dependsOn]
    if (!field.showIfValue) return !!parentValue
    return String(parentValue).toLowerCase() === String(field.showIfValue).toLowerCase()
  }

  const getFieldTypeBadge = (type: string) => {
    switch (type) {
      case 'dropdown':
        return { label: 'Dropdown Menu', bg: 'bg-emerald-500/15 text-emerald-600 border-emerald-500/30' }
      case 'radio':
        return { label: 'Radio Choices', bg: 'bg-purple-500/15 text-purple-600 border-purple-500/30' }
      case 'checkbox':
        return { label: 'Checkbox Toggle', bg: 'bg-indigo-500/15 text-indigo-600 border-indigo-500/30' }
      case 'textarea':
        return { label: 'Textarea (Multi-line)', bg: 'bg-cyan-500/15 text-cyan-600 border-cyan-500/30' }
      case 'number':
        return { label: 'Number Input', bg: 'bg-amber-500/15 text-amber-600 border-amber-500/30' }
      case 'date':
        return { label: 'Date Picker', bg: 'bg-rose-500/15 text-rose-600 border-rose-500/30' }
      default:
        return { label: 'Single Line Text', bg: 'bg-blue-500/15 text-blue-600 border-blue-500/30' }
    }
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <SectionHeading
          eyebrow="Dynamic Platform Builder"
          title="Onboarding Form & Document Builder"
          description="Build custom registration forms, set dropdown choices, rules, tooltips, and see live interactive customer preview."
          align="left"
        />

        <div className="flex flex-wrap items-center gap-3 shrink-0">
          {/* Form Type Selector */}
          <div className="flex rounded-xl bg-card border border-border/80 p-1 shadow-elevation-xs gap-1">
            <button
              type="button"
              onClick={() => setFormType('SELLER')}
              className={`rounded-lg px-3.5 py-1.5 text-xs font-extrabold transition-all ${
                formType === 'SELLER'
                  ? 'bg-primary text-primary-foreground shadow-glow'
                  : 'text-foreground/70 hover:text-foreground hover:bg-muted/60'
              }`}
            >
              Seller Form
            </button>
            <button
              type="button"
              onClick={() => setFormType('DEALER')}
              className={`rounded-lg px-3.5 py-1.5 text-xs font-extrabold transition-all ${
                formType === 'DEALER'
                  ? 'bg-purple-600 text-white shadow-glow'
                  : 'text-foreground/70 hover:text-foreground hover:bg-muted/60'
              }`}
            >
              Dealer Form
            </button>
          </div>

          {/* View Mode Toggle: Editor | Split | Preview */}
          <div className="flex rounded-xl bg-card border border-border/80 p-1 shadow-elevation-xs gap-1">
            <button
              type="button"
              onClick={() => setActiveTab('EDITOR')}
              className={`rounded-lg px-3 py-1.5 text-xs font-extrabold transition-all ${
                activeTab === 'EDITOR'
                  ? 'bg-primary text-primary-foreground shadow-glow'
                  : 'text-foreground/70 hover:text-foreground hover:bg-muted/60'
              }`}
            >
              Editor Only
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('SPLIT')}
              className={`rounded-lg px-3 py-1.5 text-xs font-extrabold transition-all ${
                activeTab === 'SPLIT'
                  ? 'bg-primary text-primary-foreground shadow-glow'
                  : 'text-foreground/70 hover:text-foreground hover:bg-muted/60'
              }`}
            >
              Side-by-Side
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('PREVIEW')}
              className={`rounded-lg px-3 py-1.5 text-xs font-extrabold transition-all ${
                activeTab === 'PREVIEW'
                  ? 'bg-emerald-600 text-white shadow-glow'
                  : 'text-foreground/70 hover:text-foreground hover:bg-muted/60'
              }`}
            >
              Live Preview
            </button>
          </div>

          <Button onClick={handleSaveConfig} disabled={updateMutation.isPending} className="gap-2 font-bold bg-primary shadow-glow">
            <Save className="size-4" /> Save Form Schema
          </Button>
        </div>
      </div>

      {/* Main Content Layout */}
      <div className={`grid gap-6 ${activeTab === 'SPLIT' ? 'lg:grid-cols-12' : 'grid-cols-1'}`}>
        {/* LEFT / MAIN COLUMN: Form Editor & Controls */}
        {(activeTab === 'EDITOR' || activeTab === 'SPLIT') && (
          <div className={`space-y-6 ${activeTab === 'SPLIT' ? 'lg:col-span-7' : 'w-full'}`}>
            {/* Form Fields Card */}
            <Card className="border-indigo-500/30 bg-gradient-to-br from-indigo-500/5 via-card to-card backdrop-blur-xl shadow-elevation-sm">
              <CardContent className="p-6 space-y-5">
                <div className="flex items-center justify-between border-b border-border/60 pb-3">
                  <div>
                    <span className="font-display text-base font-bold text-foreground flex items-center gap-2">
                      <Sliders className="size-5 text-indigo-500" /> Configured Form Questions ({fields.length})
                    </span>
                    <p className="text-xs text-muted-foreground">Define questions, input types, choice lists, tooltips, and rules.</p>
                  </div>
                  <Button size="sm" onClick={handleAddField} className="gap-1.5 text-xs font-bold bg-indigo-600 hover:bg-indigo-700 text-white">
                    <Plus className="size-3.5" /> Add Question
                  </Button>
                </div>

                {isLoading ? (
                  <p className="text-xs text-muted-foreground">Loading form configuration...</p>
                ) : fields.length > 0 ? (
                  <div className="space-y-5">
                    {fields.map((field, idx) => {
                      const isSelectable = ['dropdown', 'radio', 'checkbox'].includes(field.type)
                      const badgeInfo = getFieldTypeBadge(field.type)

                      return (
                        <div
                          key={field.id || idx}
                          className="rounded-xl border-l-4 border-l-indigo-500 border border-border/80 bg-card p-4 space-y-4 shadow-elevation-xs hover:border-indigo-500/50 transition-all"
                        >
                          {/* Field Header Actions */}
                          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border/40 pb-2.5">
                            <div className="flex items-center gap-2">
                              <span className="flex size-6 items-center justify-center rounded-md bg-indigo-500/20 font-mono text-xs font-extrabold text-indigo-600 dark:text-indigo-400">
                                #{idx + 1}
                              </span>
                              <span className="font-bold text-sm text-foreground truncate max-w-xs">
                                {field.label || 'Untitled Question'}
                              </span>
                              <Badge className={`text-[10px] font-extrabold uppercase border ${badgeInfo.bg}`}>
                                {badgeInfo.label}
                              </Badge>
                            </div>

                            <div className="flex items-center gap-1.5">
                              {/* Reorder Buttons */}
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={idx === 0}
                                onClick={() => handleMoveField(idx, 'UP')}
                                className="size-7 p-0"
                                title="Move Question Up"
                              >
                                <ArrowUp className="size-3.5 text-foreground" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                disabled={idx === fields.length - 1}
                                onClick={() => handleMoveField(idx, 'DOWN')}
                                className="size-7 p-0"
                                title="Move Question Down"
                              >
                                <ArrowDown className="size-3.5 text-foreground" />
                              </Button>

                              <label className="flex items-center gap-1.5 text-[11px] font-bold text-foreground bg-muted/50 px-2.5 py-1 rounded-md cursor-pointer ml-1">
                                <input
                                  type="checkbox"
                                  checked={field.required ?? true}
                                  onChange={(e) => handleFieldChange(idx, 'required', e.target.checked)}
                                  className="rounded border-border text-primary"
                                />
                                Required
                              </label>

                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleRemoveField(idx)}
                                className="text-red-500 hover:bg-red-500/10 p-1 size-7 ml-1"
                                title="Delete Question"
                              >
                                <Trash2 className="size-4" />
                              </Button>
                            </div>
                          </div>

                          {/* Field Basic Details */}
                          <div className="grid gap-3 sm:grid-cols-3 text-xs">
                            <div className="space-y-1">
                              <label className="text-foreground font-bold flex items-center gap-1">
                                Question Label <span className="text-red-500">*</span>
                              </label>
                              <Input
                                value={field.label}
                                onChange={(e) => handleFieldChange(idx, 'label', e.target.value)}
                                className="bg-background text-xs"
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="text-foreground font-bold">Field ID (name)</label>
                              <Input
                                value={field.name}
                                onChange={(e) => handleFieldChange(idx, 'name', e.target.value)}
                                className="bg-background text-xs font-mono text-indigo-500"
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="text-foreground font-bold">Input Control Type</label>
                              <select
                                value={field.type}
                                onChange={(e) => handleFieldChange(idx, 'type', e.target.value)}
                                className="w-full rounded-lg border border-border/80 bg-background px-3 py-2 text-xs font-bold text-foreground focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              >
                                <option value="text">Single Line Text</option>
                                <option value="textarea">Textarea (Multi-line)</option>
                                <option value="dropdown">Dropdown Select Menu</option>
                                <option value="radio">Radio Choice List</option>
                                <option value="checkbox">Single Checkbox Toggle</option>
                                <option value="number">Number Input</option>
                                <option value="date">Date Picker</option>
                              </select>
                            </div>
                          </div>

                          {/* Options Builder for Dropdown/Radio */}
                          {isSelectable && (
                            <div className="rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-3.5 space-y-3 text-xs">
                              <div className="flex items-center justify-between">
                                <span className="font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1.5 text-xs">
                                  <ListPlus className="size-4" /> Dropdown / Radio Choice List Options ({field.options?.length ?? 0})
                                </span>
                                <Button size="sm" variant="outline" onClick={() => handleAddOption(idx)} className="h-7 text-xs px-2.5 gap-1 font-bold border-emerald-500/40 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20">
                                  <Plus className="size-3.5" /> Add Choice
                                </Button>
                              </div>

                              <div className="grid gap-2 sm:grid-cols-2">
                                {(field.options || []).map((opt: string, optIdx: number) => (
                                  <div key={optIdx} className="flex items-center gap-1.5 bg-background p-1 rounded-lg border border-emerald-500/20">
                                    <span className="text-[10px] font-mono font-bold text-emerald-500 px-1">#{optIdx + 1}</span>
                                    <Input
                                      value={opt}
                                      onChange={(e) => handleOptionChange(idx, optIdx, e.target.value)}
                                      className="h-7 text-xs bg-transparent border-0 focus-visible:ring-0"
                                      placeholder="Option text..."
                                    />
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => handleRemoveOption(idx, optIdx)}
                                      className="text-red-400 hover:text-red-600 p-1 size-6 shrink-0"
                                    >
                                      ✕
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Tooltip & Help Text */}
                          <div className="rounded-xl border border-blue-500/30 bg-blue-500/10 p-3 grid gap-3 sm:grid-cols-2 text-xs">
                            <div className="space-y-1">
                              <label className="text-blue-600 dark:text-blue-400 font-bold flex items-center gap-1">
                                <HelpCircle className="size-3.5" /> Hover Guidance Tooltip
                              </label>
                              <Input
                                value={field.tooltip ?? ''}
                                onChange={(e) => handleFieldChange(idx, 'tooltip', e.target.value)}
                                placeholder="Tooltip message on hover..."
                                className="bg-background text-xs border-blue-500/30"
                              />
                            </div>
                            <div className="space-y-1">
                              <label className="text-blue-600 dark:text-blue-400 font-bold">Bottom Helper Subtext</label>
                              <Input
                                value={field.helpText ?? ''}
                                onChange={(e) => handleFieldChange(idx, 'helpText', e.target.value)}
                                placeholder="Subtext guidelines below input..."
                                className="bg-background text-xs border-blue-500/30"
                              />
                            </div>
                          </div>

                          {/* Conditional Display Rule */}
                          <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 space-y-2 text-xs">
                            <span className="font-bold text-amber-600 dark:text-amber-400 text-xs flex items-center gap-1.5">
                              <Info className="size-3.5" /> Conditional Logic Rule (Optional)
                            </span>
                            <div className="grid gap-3 sm:grid-cols-2">
                              <div className="space-y-1">
                                <label className="text-[11px] font-semibold text-muted-foreground">Depends on Field ID</label>
                                <Input
                                  placeholder="e.g. businessType"
                                  value={field.dependsOn ?? ''}
                                  onChange={(e) => handleFieldChange(idx, 'dependsOn', e.target.value)}
                                  className="bg-background text-xs border-amber-500/30"
                                />
                              </div>
                              <div className="space-y-1">
                                <label className="text-[11px] font-semibold text-muted-foreground">Show if Parent Equals</label>
                                <Input
                                  placeholder="e.g. Private Limited"
                                  value={field.showIfValue ?? ''}
                                  onChange={(e) => handleFieldChange(idx, 'showIfValue', e.target.value)}
                                  className="bg-background text-xs border-amber-500/30"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">No questions configured yet. Click "Add Question" above.</p>
                )}
              </CardContent>
            </Card>

            {/* Documents & Workflow Controls */}
            <div className="grid gap-6 sm:grid-cols-2">
              <Card className="border-rose-500/30 bg-gradient-to-br from-rose-500/5 via-card to-card backdrop-blur-xl">
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center justify-between border-b border-border/60 pb-3">
                    <span className="font-display text-base font-bold text-rose-500 flex items-center gap-2">
                      <FileText className="size-5" /> Required Verification Docs
                    </span>
                    <Button size="sm" variant="outline" onClick={handleAddDocument} className="gap-1 text-xs font-bold border-rose-500/30 text-rose-500">
                      <Plus className="size-3" /> Add Doc
                    </Button>
                  </div>

                  <div className="space-y-3">
                    {documents.map((doc, dIdx) => (
                      <div key={doc.id || dIdx} className="rounded-xl border border-rose-500/30 bg-background p-3 space-y-2 text-xs">
                        <Input
                          value={doc.name}
                          onChange={(e) => {
                            const updated = [...documents]
                            updated[dIdx].name = e.target.value
                            setDocuments(updated)
                          }}
                          className="font-bold text-xs"
                        />
                        <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                          <span>Max Size: {doc.maxMb} MB</span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setDocuments(documents.filter((_, i) => i !== dIdx))}
                            className="text-red-400 p-1 size-6"
                          >
                            <Trash2 className="size-3.5" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card className="border-purple-500/30 bg-gradient-to-br from-purple-500/5 via-card to-card backdrop-blur-xl">
                <CardContent className="p-6 space-y-4">
                  <span className="font-display text-base font-bold text-purple-500 flex items-center gap-2 border-b border-border/60 pb-3">
                    <Settings2 className="size-5" /> Workflow Policies
                  </span>

                  <div className="space-y-3 text-xs">
                    <label className="flex items-center justify-between gap-2 cursor-pointer p-2.5 rounded-xl border border-purple-500/20 bg-background font-bold">
                      <span>Enable Auto-Approval</span>
                      <input
                        type="checkbox"
                        checked={settings.autoApproval ?? false}
                        onChange={(e) => setSettings({ ...settings, autoApproval: e.target.checked })}
                        className="rounded border-border text-purple-600 size-4"
                      />
                    </label>

                    <label className="flex items-center justify-between gap-2 cursor-pointer p-2.5 rounded-xl border border-purple-500/20 bg-background font-bold">
                      <span>Require Payment First</span>
                      <input
                        type="checkbox"
                        checked={settings.requirePayment ?? false}
                        onChange={(e) => setSettings({ ...settings, requirePayment: e.target.checked })}
                        className="rounded border-border text-purple-600 size-4"
                      />
                    </label>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* RIGHT COLUMN: Live Interactive Form Preview */}
        {(activeTab === 'PREVIEW' || activeTab === 'SPLIT') && (
          <div className={`${activeTab === 'SPLIT' ? 'lg:col-span-5' : 'w-full'}`}>
            <Card className="border-emerald-500/40 bg-gradient-to-br from-emerald-500/5 via-card to-card backdrop-blur-xl sticky top-20 shadow-elevation-md">
              <CardContent className="p-6 space-y-5">
                <div className="flex items-center justify-between border-b border-border/60 pb-3">
                  <span className="font-display text-base font-bold text-emerald-500 flex items-center gap-2">
                    <Eye className="size-5" /> Live Customer Form Preview
                  </span>
                  <Badge variant="outline" className="text-[10px] uppercase font-extrabold text-emerald-500 border-emerald-500/40 bg-emerald-500/10">
                    {formType} Onboarding
                  </Badge>
                </div>

                <p className="text-xs text-muted-foreground">
                  Interactive customer view. Test inputs, choices, tooltips, and rules in real-time.
                </p>

                <form onSubmit={(e) => e.preventDefault()} className="space-y-4 text-xs">
                  {fields.map((field, fIdx) => {
                    if (!isFieldVisibleInPreview(field)) return null

                    return (
                      <div key={field.id || fIdx} className="space-y-1.5 rounded-xl border border-border/80 bg-background p-3.5 shadow-elevation-xs">
                        <div className="flex items-center justify-between">
                          <label className="font-bold text-foreground flex items-center gap-1.5 text-xs">
                            {field.label || `Question #${fIdx + 1}`}
                            {field.required && <span className="text-red-500 font-bold">*</span>}
                          </label>
                          {field.tooltip && (
                            <span className="text-blue-500 text-[10px] font-bold flex items-center gap-0.5 cursor-help" title={field.tooltip}>
                              <HelpCircle className="size-3" /> Info
                            </span>
                          )}
                        </div>

                        {field.helpText && <p className="text-[11px] text-muted-foreground">{field.helpText}</p>}

                        {/* Input Controls */}
                        {field.type === 'text' && (
                          <Input
                            placeholder={`Enter ${field.label}...`}
                            value={previewValues[field.name] || ''}
                            onChange={(e) => setPreviewValues({ ...previewValues, [field.name]: e.target.value })}
                            className="bg-background text-xs"
                          />
                        )}

                        {field.type === 'textarea' && (
                          <textarea
                            rows={3}
                            placeholder={`Enter ${field.label}...`}
                            value={previewValues[field.name] || ''}
                            onChange={(e) => setPreviewValues({ ...previewValues, [field.name]: e.target.value })}
                            className="w-full rounded-lg border border-border/80 bg-background p-2.5 text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        )}

                        {field.type === 'dropdown' && (
                          <select
                            value={previewValues[field.name] || ''}
                            onChange={(e) => setPreviewValues({ ...previewValues, [field.name]: e.target.value })}
                            className="w-full rounded-lg border border-border/80 bg-background px-3 py-2 text-xs font-bold text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                          >
                            <option value="">-- Choose Option --</option>
                            {(field.options || []).map((opt: string, oIdx: number) => (
                              <option key={oIdx} value={opt}>
                                {opt}
                              </option>
                            ))}
                          </select>
                        )}

                        {field.type === 'radio' && (
                          <div className="space-y-1.5 pt-1">
                            {(field.options || []).map((opt: string, oIdx: number) => (
                              <label key={oIdx} className="flex items-center gap-2 cursor-pointer text-xs font-semibold">
                                <input
                                  type="radio"
                                  name={field.name}
                                  value={opt}
                                  checked={previewValues[field.name] === opt}
                                  onChange={() => setPreviewValues({ ...previewValues, [field.name]: opt })}
                                  className="text-primary"
                                />
                                <span>{opt}</span>
                              </label>
                            ))}
                          </div>
                        )}

                        {field.type === 'checkbox' && (
                          <label className="flex items-center gap-2 cursor-pointer pt-1 font-semibold">
                            <input
                              type="checkbox"
                              checked={!!previewValues[field.name]}
                              onChange={(e) => setPreviewValues({ ...previewValues, [field.name]: e.target.checked })}
                              className="rounded border-border text-primary"
                            />
                            <span>{field.label}</span>
                          </label>
                        )}
                      </div>
                    )
                  })}

                  {/* Verification Documents Upload Preview */}
                  {documents.length > 0 && (
                    <div className="space-y-3 pt-3 border-t border-border/60">
                      <span className="font-bold text-foreground flex items-center gap-1.5 text-xs">
                        <Upload className="size-3.5 text-primary" /> Required Verification Documents
                      </span>
                      {documents.map((doc, docIdx) => (
                        <div key={docIdx} className="rounded-xl border border-dashed border-rose-500/40 p-3 text-center space-y-1 bg-rose-500/5">
                          <p className="font-bold text-xs text-foreground">{doc.name}</p>
                          <p className="text-[10px] text-muted-foreground">Supported: {doc.acceptedFormats?.join(', ')} (Max {doc.maxMb}MB)</p>
                          <Button size="sm" variant="outline" type="button" className="h-7 text-[11px] mt-1 gap-1 border-rose-500/30 text-rose-500 font-bold">
                            <Upload className="size-3" /> Select File
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  <Button type="button" className="w-full font-extrabold gap-2 bg-emerald-600 hover:bg-emerald-700 text-white mt-4 shadow-glow">
                    <Send className="size-4" /> Submit {formType} Application (Test)
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  )
}
